(() => {
  if (customElements.get('crftop-hero-carousel')) return;
  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.stage = this.querySelector('[data-hero-stage]');
      this.rotation = this.querySelector('[data-hero-rotation]');
      this.status = this.querySelector('[data-hero-status]');
      this.copy = this.closest('.cft-hero');
      this.index = 0;
      this.requestNumber = 0;
      this.pointer = null;
      this.visible = true;
      this.hovered = false;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.running = this.dataset.autoplay === 'true' && !this.motion.matches;
      this.querySelectorAll('img').forEach(image => { image.draggable = false; });
      this.updateCopy();
      if (this.slides.length < 2) return;
      this.querySelector('[data-hero-controls]').hidden = false;
      this.querySelector('[data-hero-previous]').addEventListener('click', () => this.move(-1), { signal });
      this.querySelector('[data-hero-next]').addEventListener('click', () => this.move(1), { signal });
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true), { signal }));
      this.rotation.addEventListener('pointerdown', () => {
        this.rotationIntent = !this.running;
      }, { signal });
      this.rotation.addEventListener('click', event => {
        const intent = event.detail > 0 ? this.rotationIntent : undefined;
        this.running = (intent ?? !this.running) && !this.motion.matches;
        this.rotationIntent = undefined;
        this.updateRotation();
        this.schedule();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const selected = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
        if (selected >= 0) this.show(selected, true);
      }, { signal });
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.schedule();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.schedule();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.running = false;
        this.updateRotation();
        this.schedule();
      }, { signal });
      this.stage.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        this.pointer = { x: event.clientX, y: event.clientY };
        this.stage.setPointerCapture(event.pointerId);
      }, { signal });
      this.stage.addEventListener('pointerup', event => {
        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.x;
        const dy = event.clientY - this.pointer.y;
        this.pointer = null;
        if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy)) this.move(dx < 0 ? 1 : -1);
      }, { signal });
      this.stage.addEventListener('pointercancel', () => { this.pointer = null; }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.running = false;
        this.updateRotation();
        this.schedule();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      });
      this.observer.observe(this);
      this.updateRotation();
      this.warmNext();
      this.schedule();
    }
    move(step) { this.show(this.index + step, true); }
    async show(index, manual = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const request = ++this.requestNumber;
      if (manual) {
        this.running = false;
        this.updateRotation();
        this.schedule();
      }
      const image = this.slides[next].querySelector('img');
      if (image) {
        image.loading = 'eager';
        try { await image.decode(); } catch {
          this.schedule();
          return;
        }
      }
      if (!this.isConnected || request !== this.requestNumber) return;
      this.index = next;
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.hidden = !active;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.dots[position].setAttribute('aria-pressed', String(active));
      });
      this.updateCopy();
      if (manual) {
        this.running = false;
        this.status.textContent = `Image ${this.index + 1} of ${this.slides.length}`;
        this.updateRotation();
      }
      this.warmNext();
      this.schedule();
    }
    updateCopy() {
      const data = this.slides[this.index]?.dataset;
      if (!data || !this.copy || !('slideHeading' in data)) return;
      const heading = this.copy.querySelector('[data-hero-copy-heading]');
      const description = this.copy.querySelector('[data-hero-copy-description]');
      const link = this.copy.querySelector('[data-hero-copy-link]');
      const fill = (node, value) => {
        if (!node) return;
        node.replaceChildren();
        String(value || '').split('\n').forEach((part, index) => {
          if (index) node.append(document.createElement('br'));
          node.append(document.createTextNode(part));
        });
      };
      fill(heading, data.slideHeading);
      if (description) { fill(description, data.slideDescription); description.hidden = !data.slideDescription; }
      if (link) {
        const label = link.querySelector('[data-hero-copy-label]');
        if (label) label.textContent = data.slideLinkLabel || '';
        link.hidden = !data.slideLink || !data.slideLinkLabel;
        if (data.slideLink) link.setAttribute('href', data.slideLink);
      }
    }
    warmNext() {
      const image = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (image) image.loading = 'eager';
    }
    updateRotation() {
      this.rotation.disabled = this.motion.matches;
      this.rotation.setAttribute('aria-label', this.running ? 'Pause image rotation' : 'Start image rotation');
      this.rotation.querySelector('[data-hero-pause]').hidden = !this.running;
      this.rotation.querySelector('[data-hero-play]').hidden = this.running;
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.running || !this.visible || this.hovered || document.hidden || this.motion.matches) return;
      const delay = Math.max(5, Number(this.dataset.interval) || 6) * 1000;
      this.timer = setTimeout(() => this.show(this.index + 1), delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.observer?.disconnect();
    }
  }
  customElements.define('crftop-hero-carousel', HeroCarousel);
})();
